import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Modal } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Multibanco from "./Multibanco";
import Paypal from "./PayPal";
import Stripe from "./Stripe";
import MBWay from "./MBWay";
import CofidisPay from "./CofidisPay";
import CustomButton from "Components/CustomButton";
import SelectPaymentMethodModal from "../../Shared/SelectPaymentMethodModal";
import { store, appendParamsToUrl } from "../../../utils/helper";
import { payment_redirect_page_prefill_post, payment_check_get_v2 } from "../../../api/payments";
import { isNilOrEmpty } from "@salespark/toolkit";

import mb from "../../../assets/images/mb.2.png";
import mbway from "../../../assets/images/mbway.2.png";
import creditcard from "../../../assets/images/creditcard.2.png";
import paypal from "../../../assets/images/paypal.2.png";
import klarna from "../../../assets/images/klarna.png";
import cofidispay from "../../../assets/images/cofidispay.png";

import { pixelEventPurchase } from "utils/pixels";

const { Title } = Typography;
const isWidget = window?.location?.hash?.substr(1) === "widget";

const Payment = ({ productData, data, actionHandler, step, customStyle }) => {
  const { t } = useTranslation();
  const [paid, setPaid] = useState(false);
  const [redirectToken, setRedirectToken] = useState(undefined);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [showPurchaseCompleted, setShowPurchaseCompleted] = useState(false);
  const [checkResponse, setCheckResponse] = useState(null);
  const [lastCheckPayment, setLastCheckPayment] = useState(null);
  const [selectPaymentMethod, setSelectPaymentMethod] = useState({ visible: false });
  const [showChangeMethod, setShowChangeMethod] = useState(false);

  const [redirectModalMessage, setRedirectModalMessage] = useState(t("payment.redirectModalDefaultMessage"));
  // const [statusAction, setStatusAction] = useState(data?.status?.action);
  // const [statusData, setStatusData] = useState(data?.status?.data);

  const paymentMethod = data?.payment_method;
  const paymentMethodId = data?.payment_method.method_id;
  const purchaseProducts = data?.purchase_products;

  const statusAction = data?.status?.action;
  const statusData = data?.status?.data;

  let timerCheck;
  let beatCounter = 1; // param for checkPayment (every 5 cyles) - for SIBS active check

  const titles = {
    creditcard: t("payment.titleCreditcard"),
    mb: t("payment.titleMb"),
    paypal: t("payment.titlePaypal"),
    mbway: t("payment.titleMbway"),
    klarna: t("payment.titleKlarna"),
    cofidispay: t("payment.titleCofidispay"),
  };

  const getMethodImage = (method_id) => {
    switch (method_id) {
      case "mbway":
        return mbway;
      case "mb":
        return mb;
      case "creditcard":
        return creditcard;
      case "paypal":
        return paypal;
      case "klarna":
        return klarna;
      case "cofidispay":
        return cofidispay;
      default:
        return "";
    }
  };

  useEffect(() => {
    if (statusAction === "wait-for-payment") {
      validateAndInitCheckoutPayment();
    }
    // setStatusAction(data?.status?.action);
    // setStatusData(data?.status?.data);
  }, [statusAction]);

  useEffect(() => {
    if (redirectToken !== undefined) {
      let total = data?.purchase_products?.reduce((acc, item) => acc + item.price, 0);
      total = parseFloat(total.toFixed(2));

      pixelEventPurchase({ ...data, value: total });
      clearTimeout(timerCheck);

      let customPage = false;
      let redirect_url = `/billing/${data?.purchase_id}/?token=${redirectToken}`;
      let redirect_text = t("payment.redirectValidating");

      if (data?.thankyou_page?.is_external === true && isNilOrEmpty(data?.thankyou_page?.url) === false) {
        customPage = true;
        redirect_text = t("payment.redirectCompleted");
      }

      if (isWidget && !customPage) {
        redirect_text = null;
      }
      let timeout = isWidget ? 4000 : 4000;

      if (customPage) {
        timeout = isWidget ? 1000 : 2500;
      }

      if (redirect_text !== null) setRedirectModalMessage(redirect_text);

      setTimeout(() => {
        // Is normal pageview
        if (!isWidget) {
          if (customPage) {
            redirectToCustomPage(data?.purchase_id, data?.thankyou_page?.url, isWidget);
            return;
          } else {
            window.location.href = redirect_url;
            return;
          }
        }

        // Is normal pageview
        if (isWidget) {
          if (customPage) {
            redirectToCustomPage(data?.purchase_id, data?.thankyou_page?.url, isWidget);
            return;
          } else {
            window.location.href = `/${productData?.product_id}/purchase-completed/?token=${redirectToken}`;
            return;
          }
        }
      }, timeout);
    }
  }, [redirectToken]);

  const redirectToCustomPage = async (purchase_id, thankyou_page_url, isWidget) => {
    // const getPrefillResponse= await dbRedirectPagePrefill()

    const prefillResponse = await dbRedirectPagePrefill({ purchase_id: purchase_id });
    if (!prefillResponse.status) {
      redirectPage(thankyou_page_url, isWidget);
      return;
    }

    const prefillData = prefillResponse.data;
    const redirect_url = appendParamsToUrl(thankyou_page_url, { sp: prefillData.data });
    redirectPage(redirect_url, isWidget);
  };

  const redirectPage = (url, isWidget) => {
    if (isWidget) {
      window.top.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  const validateAndInitCheckoutPayment = () => {
    if (paymentMethodId === "klarna" && data?.price_option?.price_method !== "one_time") {
      actionHandler("move", 1);
      Modal.error({
        title: t("payment.errorModalTitle"),
        content: t("payment.klarnaOneTimeOnly"),
      });
      return <></>;
    }

    if (paymentMethodId === "paypal" && data?.price_option?.price_method !== "one_time") {
      actionHandler("move", 1);
      Modal.error({
        title: t("payment.errorModalTitle"),
        content: t("payment.paypalOneTimeOnly"),
      });
      return <></>;
    }

    if (paymentMethodId === "cofidispay" && data?.price_option?.price_method !== "one_time") {
      actionHandler("move", 1);
      Modal.error({
        title: t("payment.errorModalTitle"),
        content: t("payment.cofidispayOneTimeOnly"),
      });
      return <></>;
    }
    initCheckPayment();
  };

  const initCheckPayment = async () => {
    if (statusAction === "wait-for-payment") {
      let checks = store("GET", "checks", "INT");
      checks++;
      store("SET", "checks", checks);
      clearTimeout(timerCheck);
      timerCheck = setTimeout(() => {
        // Check if now is less then timetout loop (against lastCheckPayment)

        checkPayment();
      }, 1000);
    } else {
      // If not waiting for payment, we can reset the checks
      store("SET", "checks", 0);
      clearTimeout(timerCheck);
      setCheckResponse(null);
    }
  };

  const checkPayment = async () => {
    let isPaid = false;
    let beat = false;
    beatCounter++;

    if (beatCounter > 3) {
      beatCounter = 1;
      beat = true;
    }

    if (["Declined", "Timeout", "Error", "Error-E0506"].includes(checkResponse) === true) {
      clearTimeout(timerCheck);
      setCheckResponse(null);
      return;
    }

    payment_check_get_v2(statusData.method_id, statusData.payment_unique_id, beat)
      .then((response) => {
        if (response.data.paid === true) {
          if (!paid) {
            actionHandler("update_status", {
              action: "paid",
              data: {
                purchase_id: response.data.purchase_id,
                entity: response.data.entity,
                reference: response.data.reference,
                phone: response.data.phone,
              },
            });
          }
          setPaid(true);
          isPaid = true;

          if (response.data.token !== undefined) {
            setRedirectToken(response.data.token);
            setShowRedirectModal(true);
          }
        } else {
          if (response.data?.status !== "") {
            setCheckResponse(response.data?.status);
          }
        }
      })
      .catch((error) => {
        //console.log("error", error);
      })
      .finally(() => {
        if (isPaid === false) {
          if (statusAction === "wait-for-payment") {
            setTimeout(() => {
              initCheckPayment();
            }, 5000);
          }
        }
      });
  };

  const dbRedirectPagePrefill = async (data) => {
    return new Promise((resolve) => {
      try {
        payment_redirect_page_prefill_post(data)
          .then((res) => resolve({ status: true, data: res }))
          .catch((err) => resolve({ status: false, data: err }));

        // Error handling
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  // console.log("Payment", paymentMethodId, purchaseProducts);

  return (
    <>
    <div className="payment-container">
      <div className="payment-container-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="title">
          <Title level={3}>{titles[paymentMethodId]}</Title>
        </div>
        <div className="payment-icon">
          <img height={52} src={getMethodImage(paymentMethodId)} alt={paymentMethod?.name} />
        </div>
      </div>
      {paymentMethodId === "mb" && <Multibanco productData={productData} data={data} actionHandler={actionHandler} customStyle={customStyle} />}
      {paymentMethodId === "mbway" && (
        <MBWay
          productData={productData}
          data={data}
          actionHandler={actionHandler}
          customStyle={customStyle}
          setShowChangeMethod={setShowChangeMethod}
          checkResponse={checkResponse}
          setCheckResponse={setCheckResponse}
          stopCheckHandler={() => {
            clearTimeout(timerCheck);

            actionHandler("update_status", {
              action: "checkout",
              data: {},
            });
          }}
        />
      )}

      {paymentMethodId === "creditcard" && (
        <Stripe method="card" productData={productData} data={data} actionHandler={actionHandler} customStyle={customStyle} setShowChangeMethod={setShowChangeMethod} />
      )}

      {paymentMethodId === "klarna" && <Stripe method="klarna" productData={productData} data={data} actionHandler={actionHandler} customStyle={customStyle} setShowChangeMethod={setShowChangeMethod} />}

      {paymentMethodId === "paypal" && <Paypal productData={productData} data={data} actionHandler={actionHandler} customStyle={customStyle} setShowChangeMethod={setShowChangeMethod} />}

      {paymentMethodId === "cofidispay" && <CofidisPay productData={productData} data={data} actionHandler={actionHandler} customStyle={customStyle} setShowChangeMethod={setShowChangeMethod} />}
      <Modal closable={false} title={t("payment.redirectModalTitle")} visible={showRedirectModal} okText={t("payment.redirectModalOkText")} footer={null}>
        <p>
          <LoadingOutlined />
          &nbsp;&nbsp;&nbsp;{redirectModalMessage}
        </p>
      </Modal>
    </div>
    {showChangeMethod && (
      <div style={{ textAlign: "center", marginTop: "15px" }}>
        <CustomButton buttonStyle={customStyle} type="link" onClick={() => setSelectPaymentMethod({ visible: true })}>
          <span style={{ fontSize: "13px" }}>
            {t(paymentMethodId === "creditcard" || paymentMethodId === "klarna" ? "stripe.changeMethodButton" : `${paymentMethodId}.changeMethodButton`)}
          </span>
        </CustomButton>
      </div>
    )}
    <SelectPaymentMethodModal
      data={data}
      actionHandler={actionHandler}
      visible={selectPaymentMethod.visible}
      close={() => setSelectPaymentMethod({ visible: false })}
      customStyle={customStyle}
    />
    </>
  );
};

export default Payment;
